import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    QueryList,
    ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { LoggerService } from '@klickdata/core/application';
import { AuthService } from '@klickdata/core/auth';
import { MobileService } from '@klickdata/core/mobile';
import { AppScope } from '@klickdata/core/resource';
import { Utils } from '@klickdata/core/util';
import { BehaviorSubject, Observable, Subject, merge } from 'rxjs';
import { filter, map, shareReplay, takeUntil } from 'rxjs/operators';
import { GlobalFilterProperty } from '../global-filters-types.enum';
import { Filter, FilterCollection } from './filter';
import { FilterBase } from './filter-base';
import { JoinFilterComponent } from './join-filter/join-filter.component';

@Component({
    selector: 'app-table-filter',
    templateUrl: './table-filter.component.html',
    styleUrls: ['./table-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableFilterComponent implements AfterContentInit, AfterViewInit, OnDestroy {
    @Input() cacheScope: AppScope;
    @Input() color: string;
    @Input() inGlobalFilter = false;
    @Input() filtersTogglesInOnBtn = false;
    @Input() toggleFilterButtons = true;
    @Input() defaultFilter = GlobalFilterProperty.SEARCH;
    @Output() filterChange: EventEmitter<FilterCollection<string | number>> = new EventEmitter<
        FilterCollection<string | number>
    >();
    @ContentChildren(FilterBase, { descendants: true }) contentFilterComps: QueryList<FilterBase>;
    @ViewChild(JoinFilterComponent) joinFilterComp: JoinFilterComponent;

    // Combined list of all filter components (content + join filter)
    get filterComps(): FilterBase[] {
        const contentFilters = this.contentFilterComps?.toArray() || [];
        const joinFilter = this.joinFilterComp ? [this.joinFilterComp] : [];
        return [...contentFilters, ...joinFilter];
    }
    private destroy: Subject<boolean> = new Subject<boolean>();
    private _selectedFilter: FormControl<GlobalFilterProperty | string>;
    get selectedFilter(): FormControl<GlobalFilterProperty | string> {
        return this._selectedFilter;
    }
    public isMobile: Observable<boolean>;
    protected _filter: FilterCollection<string | number> = new FilterCollection();
    public get filter(): FilterCollection<string | number> | null {
        return this._filter;
    }
    public activeAppliedFilters: Filter<string | number>[] = [];
    GlobalFilterProperty = GlobalFilterProperty;
    Utils = Utils;
    private cacheLoaded: boolean;
    public advancedFiltering: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private suppressRefresh = false;
    private pendingRefresh = false;

    constructor(
        protected cdRef: ChangeDetectorRef,
        protected auth: AuthService,
        protected mobile: MobileService,
        protected logger: LoggerService
    ) {
        this.isMobile = this.mobile.isMobile().pipe(takeUntil(this.destroy), shareReplay());
    }
    public ngAfterContentInit(): void {
        this._selectedFilter = new FormControl(this.defaultFilter);

        const obs: Observable<Filter<string | number>>[] = [];
        this.filterComps?.forEach((filterComp) => {
            obs.push(this.mapFilterChange(filterComp));
            if (filterComp.filterRemoval) {
                obs.push(this.removeFilter(filterComp.filterRemoval));
            }
        });

        merge(...obs)
            .pipe(takeUntil(this.destroy))
            .subscribe((filtr) => {
                this.prepareActiveAppliedFilters(filtr);
                this.refresh();
                this.cacheLoaded = true;
            });

        this.initCache();
    }

    public ngAfterViewInit(): void {
        // Handle join filter component after view init
        if (this.joinFilterComp) {
            // Add the join filter to the observable array (both filterChange and filterRemoval)
            const obs: Observable<Filter<string | number>>[] = [];
            obs.push(this.mapFilterChange(this.joinFilterComp));
            if (this.joinFilterComp.filterRemoval) {
                obs.push(this.removeFilter(this.joinFilterComp.filterRemoval));
            }

            merge(...obs)
                .pipe(takeUntil(this.destroy))
                .subscribe((filtr) => {
                    this.prepareActiveAppliedFilters(filtr);
                    this.refresh();
                });

            // Initialize cache for join filter
            this.joinFilterComp.cacheScope = this.cacheScope;
            const mfltr = this.joinFilterComp.mapCache((cachedFltr) => this.pushChangeWithoutEmit(cachedFltr));
            if (mfltr) {
                this.activateAdvancedFilteringFromCache();
                setTimeout(() => this.prepareActiveAppliedFilters(mfltr));
            }
        }
    }

    private initCache() {
        this.filterComps.forEach((fltr) => {
            fltr.cacheScope = this.cacheScope;
            const mfltr = fltr.mapCache((cachedFltr) => this.pushChangeWithoutEmit(cachedFltr));
            setTimeout(() => this.prepareActiveAppliedFilters(mfltr));
        });
    }

    public filterByModel(
        filter: Filter<string | number> | Filter<string | number>[],
        withClearOfAllOtherFilters = false,
        addToCache = true
    ) {
        const filters = Utils.wrapInArray(filter);
        const hasJoinFilter = filters.some(
            (fltr) => fltr.property === GlobalFilterProperty.JOIN_TYPE && !!fltr.items?.length
        );

        if (hasJoinFilter && !this.advancedFiltering.value) {
            this.setAdvancedFilteringState(true, { fromCache: true });
        }

        if (withClearOfAllOtherFilters) {
            this.activeAppliedFilters
                .filter((afltr) => -1 === filters.findIndex((fltr) => fltr.property === afltr.property))
                .forEach((fltr) => this.remove(fltr));
        }
        filters.forEach((fltr) => this.updateActiveAppliedFromFilter(fltr, addToCache));
        this.refresh();
    }

    private updateActiveAppliedFromFilter(filter: Filter<string | number>, addToCache = true) {
        const filterComp = this.getFilterComponentByProperty(filter.property);
        if (filterComp) {
            const mfltr = filterComp.mapByFilter(filter, (fltr) => this.pushChangeWithoutEmit(fltr), addToCache);
            this.prepareActiveAppliedFilters(mfltr);
        }
    }

    private mapFilterChange(fltrBase: FilterBase): Observable<Filter<string | number>> {
        return fltrBase.mapFilterChange(
            (fltr) => this.checkLoadedFromCache(fltr),
            (fltr) => this.pushChangeWithoutEmit(fltr)
        );
    }

    private checkLoadedFromCache(fltr: Filter<string | number>): boolean {
        return this.cacheLoaded || this.getFilterIndex(fltr.property) === -1 || !fltr.lazy;
    }

    prepareActiveAppliedFilters(filterInAction: Filter<string | number>): void {
        if (!filterInAction?.items) {
            return;
        }

        /**
         * 4- types of filters.
         * 1. normal
         * 2. isChip
         * 3. Composite
         * 4. filterInAction => one show in active many remove from active.
         */
        const index = this.getActiveFilterIndex(filterInAction.property);
        // already exists in active filters
        if (index !== -1) {
            const currentFltr = this.activeAppliedFilters[index];
            if (filterInAction.items?.length > 1 && filterInAction.singleActive) {
                this.activeAppliedFilters.splice(index, 1);
            } else {
                this.updateCurrentActiveFilter(currentFltr, filterInAction);
            }
        } else {
            // Don't add filters with empty items...
            if (!filterInAction.items.length || filterInAction.items.every((item) => !item)) {
                return;
            }

            // Don't add singleActive filter with many items...
            if (filterInAction.singleActive && filterInAction.items.length > 1) {
                return;
            }
            this.activeAppliedFilters.push(filterInAction);
        }

        // Remove join filter if there are less than 2 non-join filters
        this.removeJoinFilterIfNeeded();

        // Initialize join filter with default value if it should be shown
        this.initializeJoinFilterIfNeeded();

        this.cdRef.markForCheck();
    }

    private updateCurrentActiveFilter(currentFltr: Filter<string | number>, filterInAction: Filter<string | number>) {
        if (!filterInAction.items.length || filterInAction.items.every((item) => !item)) {
            this.remove(currentFltr);
        } else {
            currentFltr.items = filterInAction.items;
            currentFltr.chips = filterInAction.chips;
        }
    }

    public remove(removedFilter: Filter<string | number>) {
        const index = this.getActiveFilterIndex(removedFilter.property);
        if (index === -1) {
            // console.log('not found');
            return;
        }
        this.activeAppliedFilters.splice(index, 1);
        const filterComp = this.getFilterComponentByProperty(removedFilter.property);
        if (filterComp) {
            filterComp.remove();
        }

        // Remove join filter if there are less than 2 non-join filters
        this.removeJoinFilterIfNeeded();

        this.cdRef.markForCheck();
    }

    private pushChangeWithoutEmit(mappedFilter: Filter<string | number>) {
        const index = this.getFilterIndex(mappedFilter.property);
        if (index !== -1) {
            this.filter.filters.splice(index, 1);
        }
        this.filter.filters.push(mappedFilter);
    }

    /**
     * Get filter index in filters colections
     * @param property Filter.property
     * @returns index of given filter in a collection.
     */
    private getFilterIndex(property: string): number {
        return this.getIndex(this.filter.filters, property);
    }

    private getActiveFilterIndex(property: string): number {
        return this.getIndex(this.activeAppliedFilters, property);
    }

    private getIndex(filters: Filter<string | number>[], property: string): number {
        return filters.findIndex((value: Filter<string | number>) => value.property === property);
    }

    public getFilterValue(property: string): (string | number)[] {
        return this.filter.filters.find((value: Filter<string | number>) => value.property === property).items;
    }

    ngOnDestroy() {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }

    public createOrUpdateWithoutEmitFilter(filters: Filter<string | number>[], ignored?: string[]) {
        filters.forEach((fltr) => this.pushChangeWithoutEmit(fltr));
        if (ignored) {
            this.removeFromFilters(ignored);
        }
    }

    public createOrUpdateWithFilterByModel(
        filters: Filter<string | number>[],
        modelFilters?: Filter<string | number>[]
    ) {
        filters.forEach((fltr) => this.pushChangeWithoutEmit(fltr));
        if (!!modelFilters?.length) {
            modelFilters.forEach((fltr) => this.updateActiveAppliedFromFilter(fltr));
        } else {
        }
        this.refresh();

        this.cdRef.markForCheck();
    }

    /**
     * Remove filters from applied filters.
     * @param ignored filter array to be removed for applied filters.
     */
    private removeFromFilters(ignored: string[]) {
        for (const property of ignored) {
            const index = this.getFilterIndex(property);
            if (index !== -1) {
                this.filter.filters.splice(index, 1);
            }
        }
    }

    public createOrUpdateFilter(filters: Filter<string | number>[], ignored?: string[]) {
        this.createOrUpdateWithoutEmitFilter(filters, ignored);
        this.refresh();
        this.cdRef.markForCheck();
    }

    public ignoreFilter(ignored: string[], withoutEmission?: boolean) {
        this.removeFromFilters(ignored);
        if (!withoutEmission) {
            this.refresh();
        }
        this.cdRef.markForCheck();
    }

    public refresh() {
        if (this.suppressRefresh) {
            this.pendingRefresh = true;
            return;
        }
        this.pendingRefresh = false;
        this.filterChange.emit(this.filter);
    }

    public removeFilter(filterRemoval: EventEmitter<Filter<string | number>>): Observable<Filter<string | number>> {
        return filterRemoval.pipe(
            map((fltr) => [fltr, this.getFilterIndex(fltr.property)] as [Filter<string | number>, number]),
            filter(([fltr, index]) => index !== -1),
            map(([removedFilter, index]) => removedFilter)
        );
    }

    public clear(excludes: Filter<string | number>[] = []): void {
        this.filterComps
            ?.filter((filterComp) => -1 === excludes.findIndex((exclude) => filterComp.equals(exclude)))
            .forEach((filterComp) => filterComp.remove());
    }

    public selectAll(): void {
        this.filterComps?.forEach((filterComp) => filterComp.selectAll());
    }

    public setActiveFilterWithQuery(property: GlobalFilterProperty, query: string): void {
        this.selectedFilter.setValue(property);
        const filterComp = this.getFilterComponentByProperty(property);
        filterComp?.setQuery(query);
    }

    public setActiveFilterWithSelectedOption(property: GlobalFilterProperty, value: string | number): void {
        this.selectedFilter.setValue(property);
        const filterComp = this.getFilterComponentByProperty(property);
        filterComp?.setSelectedOption(value);
    }

    public setActiveFilter(property: GlobalFilterProperty): void {
        this.selectedFilter.setValue(property);
    }

    public getFilterComponentByProperty(property: GlobalFilterProperty | string): FilterBase {
        return this.filterComps?.find((filterComp) => filterComp.filter.property === property);
    }

    public getFilterByProperty(property: GlobalFilterProperty | string): Filter<string | number> {
        const filterComp = this.getFilterComponentByProperty(property);
        return filterComp?.filter;
    }

    public isDefaultFilter(property: GlobalFilterProperty | string): boolean {
        return property === GlobalFilterProperty.SEARCH;
    }

    hasActiveFilters(): boolean {
        return this.activeAppliedFilters.length > 0;
    }

    /**
     * Determines if the join filter should be shown
     * Show join filter only when there are more than one active filters items (excluding the join filter itself)
     */
    shouldShowJoinFilter(): boolean {
        // Count active filters excluding the join filter
        const nonJoinFiltersItems = this.activeAppliedFilters
            .filter((filter) => filter.property !== GlobalFilterProperty.JOIN_TYPE)
            .map((filter) => filter.items)
            .flat().length;
        // return nonJoinFilters.map((filter) => filter.items).flat().length > 1;
        return nonJoinFiltersItems > 1;
    }

    /**
     * Removes the join filter from activeAppliedFilters if there are less than 2 non-join filters
     */
    private removeJoinFilterIfNeeded(): void {
        const nonJoinFiltersItems = this.activeAppliedFilters
            .filter((filter) => filter.property !== GlobalFilterProperty.JOIN_TYPE)
            .map((filter) => filter.items)
            .flat().length;
        if (nonJoinFiltersItems < 2) {
            // Find and remove the join filter
            const joinFilterIndex = this.activeAppliedFilters.findIndex(
                (filter) => filter.property === GlobalFilterProperty.JOIN_TYPE
            );
            // Not emitting filter removal event because we want to keep the join filter in the activeAppliedFilters for the next time we need to show it
            if (joinFilterIndex !== -1) {
                this.joinFilterComp?.clear(false);
            }
        }
    }

    /**
     * Initializes the join filter with its default value ('or') when it should be shown
     * but doesn't have any items set yet.
     * Updates the filter state without emitting to avoid duplicate requests.
     * The existing refresh() call from the subscription will handle emitting the updated filter collection.
     */
    private initializeJoinFilterIfNeeded(): void {
        if (!this.shouldShowJoinFilter() || !this.joinFilterComp) {
            return;
        }

        const joinFilterIndex = this.activeAppliedFilters.findIndex(
            (filter) => filter.property === GlobalFilterProperty.JOIN_TYPE
        );

        // If join filter should be shown but doesn't have items, initialize it with default value
        const hasItems = this.joinFilterComp.filter.items && this.joinFilterComp.filter.items.length > 0;
        if (joinFilterIndex === -1 || !hasItems) {
            // Set the default value in the filter
            this.joinFilterComp.filter.items = ['or'];
            // Ensure the form control is also set (without emitting to prevent duplicate events)
            this.joinFilterComp.joinFilterControl.setValue('or', { emitEvent: false });
            // Add to activeAppliedFilters if not already there
            if (joinFilterIndex === -1) {
                this.activeAppliedFilters.push(this.joinFilterComp.filter);
            }
            // Update the filter collection without emitting
            // The existing refresh() call from the subscription will handle emitting
            this.pushChangeWithoutEmit(this.joinFilterComp.filter);
        }
    }
    public onToggleAdvancedFiltering(): void {
        const nextState = !this.advancedFiltering.value;
        this.setAdvancedFilteringState(nextState);
    }

    private activateAdvancedFilteringFromCache(): void {
        this.setAdvancedFilteringState(true, { fromCache: true });
    }

    private setAdvancedFilteringState(isAdvancedFiltering: boolean, options: { fromCache?: boolean } = {}): void {
        const previousState = this.advancedFiltering.value;
        if (previousState === isAdvancedFiltering && !options.fromCache) {
            return;
        }

        this.advancedFiltering.next(isAdvancedFiltering);
        this.filterComps.forEach((filterComp) =>
            filterComp.onToggleAdvancedFiltering(isAdvancedFiltering, options.fromCache ?? false)
        );

        if (!isAdvancedFiltering && !!this.activeAppliedFilters?.length && !options.fromCache) {
            this.runWithSuppressedRefresh(() => {
                this.activeAppliedFilters.length = 0;
                this.clear();
            });
        }
    }

    private runWithSuppressedRefresh(callback: () => void): void {
        const wasSuppressed = this.suppressRefresh;
        this.suppressRefresh = true;
        try {
            callback();
        } finally {
            this.suppressRefresh = wasSuppressed;
            if (!this.suppressRefresh && this.pendingRefresh) {
                this.pendingRefresh = false;
                this.filterChange.emit(this.filter);
            }
        }
    }
}
